// src/pages/Admin_AllAdmins.jsx
import { useEffect, useState, useMemo } from "react";
import axios from "axios";
import SuperAdminNav from "../components/superAdminNav";
import Time from "../components/time";
import Swal from "sweetalert2";
import { Link } from "react-router-dom";
import ResetPasswordModal from "../components/resetPassword";
import CompanyDetailsModal from "../components/companyDetailsModal";

import {
  Search,
  Building2,
  ExternalLink,
  KeyRound,
  PencilLine,
  Trash2,
  Database,
  Loader2,
  Plus,
} from "lucide-react";

const API = "https://superadmin.nissiindia.com";

export default function Admin_AllAdmins() {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  // Reset password modal state
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [pwTenantId, setPwTenantId] = useState(null);
  const [pwUsername, setPwUsername] = useState("");

  // Company details modal state
  const [coModalOpen, setCoModalOpen] = useState(false);
  const [coTenantId, setCoTenantId] = useState(null);
  const [coUsername, setCoUsername] = useState("");
  const [coCompanyName, setCoCompanyName] = useState("");

  // client-side pagination
  const [page, setPage] = useState(1);
  const perPage = 10;

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/api/admins/all`, {
        params: { page: 1, pageSize: 10000 },
      });
      setRows(r.data?.rows || []);
      setPage(1);
    } catch (e) {
      console.error(e);
      Swal.fire("Error", e?.response?.data?.error || e.message, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSearch = (e) => setSearch(e.target.value.toLowerCase());

  const filtered = useMemo(() => {
    if (!search) return rows;
    return rows.filter((r) =>
      String(r.tenantCode || "").toLowerCase().includes(search) ||
      String(r.domain || "").toLowerCase().includes(search) ||
      String(r.username || "").toLowerCase().includes(search) ||
      String(r.company_name || "").toLowerCase().includes(search) ||
      String(r.email || "").toLowerCase().includes(search) ||
      String(r.phone || "").toLowerCase().includes(search) ||
      String(r.address || "").toLowerCase().includes(search)
    );
  }, [rows, search]);

  const first = (page - 1) * perPage;
  const current = filtered.slice(first, first + perPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));

  // Delete handler
  const doDelete = async (tenantId, username) => {
    const ok = await Swal.fire({
      icon: "warning",
      title: "Delete admin?",
      text: `This will permanently delete '${username}' from tenant #${tenantId}.`,
      showCancelButton: true,
      confirmButtonText: "Delete",
      confirmButtonColor: "#e53935",
    }).then((r) => r.isConfirmed);
    if (!ok) return;

    try {
      await axios.delete(
        `${API}/api/admins/${tenantId}/${encodeURIComponent(username)}`
      );
      Swal.fire("Deleted", "Admin removed successfully.", "success");
      await load();
    } catch (e) {
      console.error(e);
      Swal.fire("Error", e?.response?.data?.error || e.message, "error");
    }
  };

  // Reset password modal
  const openPwModal = (tenantId, username) => {
    setPwTenantId(tenantId);
    setPwUsername(username);
    setPwModalOpen(true);
  };
  const onPwResetOk = async () => {
    setPwModalOpen(false);
    await load();
  };

  // Company modal
  const openCompanyModal = (tenantId, username, companyName) => {
    setCoTenantId(tenantId);
    setCoUsername(username);
    setCoCompanyName(companyName);
    setCoModalOpen(true);
  };

  const statusBadge = (active) =>
    active
      ? "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
      : "bg-rose-50 text-rose-700 ring-1 ring-rose-200";

  // --- product helpers (derived from DB name, no backend change) ---
  const productLabel = (dbName) => {
    const n = String(dbName || "").toLowerCase();
    if (n.startsWith("patrolv_t_")) return "Patrol Visit";
    if (n.startsWith("patrol_t_")) return "EBD Patrol";
    return "Unknown";
  };

  const productBadgeClass = (dbName) => {
    const label = productLabel(dbName);
    if (label === "Patrol Visit") {
      return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
    }
    if (label === "EBD Patrol") {
      return "bg-sky-50 text-sky-700 ring-1 ring-sky-200";
    }
    return "bg-slate-50 text-slate-700 ring-1 ring-slate-200";
  };

  return (
    <div className="min-h-screen bg-slate-100">
      <div className="flex flex-col md:flex-row md:h-screen md:overflow-hidden">
        <SuperAdminNav />

        <main className="w-full md:w-3/4 md:h-full md:overflow-auto py-6 px-4 sm:px-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-slate-900 text-white">
                <Database className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-slate-900">
                  All Tenants – Admin Credentials
                </h2>
                <p className="text-sm text-slate-500">
                  Manage admin accounts across tenants
                </p>
              </div>
            </div>
            <div className="text-sm text-slate-500">
              <Time />
            </div>
          </div>

          <div className="mt-5 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <Link to={`/superAdmin/tenant_Bootstrap`}>
              <button className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500/30">
                <Plus className="h-4 w-4" />
                Add New
              </button>
            </Link>

            <div className="relative w-full sm:w-96">
              <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                placeholder="Search tenant / domain / username / email / phone / address"
                value={search}
                onChange={onSearch}
                className="w-full rounded-lg border border-slate-300 bg-white pl-9 pr-3 py-2 text-slate-900 placeholder:text-slate-400 shadow-sm focus:border-slate-400 focus:ring-2 focus:ring-blue-500/20"
              />
            </div>
          </div>

          {/* Table */}
          <div className="mt-6 overflow-x-auto rounded-xl border border-slate-200 bg-white shadow">
            <table className="min-w-[900px] w-full text-sm">
              <thead className="bg-gradient-to-r from-slate-900 to-slate-800 text-slate-100">
                <tr className="text-left">
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70 w-16">
                    S.No
                  </th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">
                    Tenant
                  </th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">
                    Product
                  </th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">
                    Username
                  </th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">
                    Company
                  </th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">
                    Users
                  </th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70">
                    Status
                  </th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70 w-32">
                    Modules
                  </th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70 w-32">
                    Mini DB
                  </th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70 w-28">
                    Reset
                  </th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70 w-24">
                    Edit
                  </th>
                  <th className="px-4 py-3 font-semibold border-b border-slate-800/70 w-28">
                    Delete
                  </th>
                </tr>
              </thead>

              <tbody className="divide-y divide-slate-100">
                {loading ? (
                  <tr>
                    <td colSpan="12" className="px-4 py-10 text-center">
                      <span className="inline-flex items-center gap-2 text-slate-500">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Loading, please wait…
                      </span>
                    </td>
                  </tr>
                ) : current.length > 0 ? (
                  current.map((r, i) => {
                    const product = productLabel(r.databaseName);
                    const isVisit = product === "Patrol Visit";

                    return (
                      <tr
                        key={`${r.tenantId}:${r.username}`}
                        className="odd:bg-white even:bg-slate-50 hover:bg-slate-100/60"
                      >
                        <td className="px-4 py-3 text-slate-500">
                          {first + i + 1}
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <Building2 className="h-4 w-4 text-slate-500" />
                            <div>
                              <div className="font-medium text-slate-900">
                                {r.tenantCode}
                              </div>
                              <div className="text-xs text-slate-500">
                                {r.databaseName}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Product */}
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${productBadgeClass(
                              r.databaseName
                            )}`}
                          >
                            {product}
                          </span>
                        </td>

                        <td className="px-4 py-3 text-slate-900">
                          {r.username}
                        </td>

                        <td className="px-4 py-3">
                          <button
                            type="button"
                            className="text-blue-600 hover:underline"
                            onClick={() =>
                              openCompanyModal(
                                r.tenantId,
                                r.username,
                                r.company_name
                              )
                            }
                            title="Show company details"
                          >
                            {r.company_name || "—"}
                          </button>
                        </td>

                        <td className="px-4 py-3 text-slate-700">
                          {r.num_users ?? "—"}
                        </td>

                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${statusBadge(
                              r.is_active
                            )}`}
                          >
                            {r.is_active ? "Active" : "Inactive"}
                          </span>
                        </td>

                        {/* Modules: hide for Patrol Visit (no module method) */}
                        <td className="px-4 py-3">
                          {isVisit ? (
                            <span className="text-xs text-slate-400">
                              N/A for Patrol Visit
                            </span>
                          ) : (
                            <Link
                              to={`/superAdmin/admin_module/${encodeURIComponent(
                                r.username
                              )}?tid=${r.tenantId}`}
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                              title="Manage module access"
                            >
                              Manage
                            </Link>
                          )}
                        </td>

                        {/* Mini DB (available for both products) */}
                        <td className="px-4 py-3">
                          <Link
                            to={`/superAdmin/mini_database?tid=${r.tenantId}`}
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            title="Open mini database for this tenant"
                          >
                            Manage
                          </Link>
                        </td>

                        {/* Reset */}
                        <td className="px-4 py-3">
                          <button
                            className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                            onClick={() => openPwModal(r.tenantId, r.username)}
                            title="Reset Password"
                          >
                            <KeyRound className="h-4 w-4" />
                          </button>
                        </td>

                        {/* Edit */}
                        <td className="px-4 py-3">
                          <Link
                            to={`/superAdmin/admin_Menu_edit/${encodeURIComponent(
                              r.username
                            )}?tid=${r.tenantId}`}
                          >
                            <button
                              className="inline-flex items-center gap-2 rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                              title="Edit"
                            >
                              <PencilLine className="h-4 w-4" />
                            </button>
                          </Link>
                        </td>

                        {/* Delete */}
                        <td className="px-4 py-3">
                          <button
                            className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-3 py-1.5 text-white shadow hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-500/30"
                            onClick={() => doDelete(r.tenantId, r.username)}
                            title="Delete"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td
                      colSpan="12"
                      className="px-4 py-10 text-center text-slate-500"
                    >
                      No Data Available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="mt-4 flex items-center justify-center gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-slate-600">
              Page {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-slate-900 shadow-sm hover:bg-slate-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </main>
      </div>

      {/* Reset Password Modal */}
      <ResetPasswordModal
        open={pwModalOpen}
        tenantId={pwTenantId}
        username={pwUsername}
        onClose={() => setPwModalOpen(false)}
        onSuccess={onPwResetOk}
      />

      {/* Company Details Modal */}
      <CompanyDetailsModal
        open={coModalOpen}
        tenantId={coTenantId}
        username={coUsername}
        companyName={coCompanyName}
        onClose={() => setCoModalOpen(false)}
      />
    </div>
  );
}
